import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import useApi from 'hooks/useApi';
import IntegrationList from './IntegrationList';
import IntegrationForm from './IntegrationForm';

// ============================================================================
// TYPESCRIPT INTERFACES
// ============================================================================

interface Step {
  uid: string;
  _id?: string;
  title: string;
}

interface IntegrationConfig {
  _id: string;
  teamUid: string;
  stepUid: string[];
  integrationType: 'webhook' | 'smartsheet';
  configFields: any[];
  createdAt: Date;
  updatedAt: Date;
}

// interface TeamData {
//   teamUid: string;
//   [key: string]: any;
// }

interface SmartSheetConfigProps {
  teamUid: string;
  client: string;
  selectedConfig?: any;
  handleCloseDialog?: any;
  onConfigSaved?: () => void;
  teamData?: any;
  onTeamFormSubmit?: (formData: any, callback: (success: boolean) => void) => void;
  loadTeamById?:(teamUid:string)=>void;
}

// ============================================================================
// MAIN SMART SHEET CONFIG COMPONENT
// ============================================================================


const SmartSheetConfig: React.FC<SmartSheetConfigProps> = ({
  teamUid,
  teamData,
  loadTeamById
}) => {
  const theme = useTheme();
  
  const [stepOptions, setStepOptions] = useState<Step[]>([]);
  const [configurations, setConfigurations] = useState<IntegrationConfig[]>([]);
  const [selectedConfigForEdit, setSelectedConfigForEdit] = useState<IntegrationConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { getFlowByTeamUid, createIntegrationConfig, showToast, updateIntegrationConfig, getIntegrationConfig, deleteIntegrationConfig, updateTeam, createTeam } = useApi();

  // ============================================================================
  // TEAM WEBHOOK CONFIG HANDLERS
  // ============================================================================

  const updateTeamApi = async (payload: any, callback: (success: boolean) => void) => {
    try {
      const response = await updateTeam(payload, teamUid);
      const { success, message } = response;
      if (success) {
        loadTeamById?.(teamUid)
        // showToast('Team configuration updated successfully', 'success');
        callback(true);
      } else {
        showToast(message || 'Failed to update team configuration', 'error');
        callback(false);
      }
    } catch (error: any) {
      showToast(error.message || 'Error updating team configuration', 'error');
      callback(false);
    }
  };

  const createTeamApi = async (payload: any, callback: (success: boolean) => void) => {
    try {
      const response = await createTeam(payload);
      const { success, message } = response;
      if (success) {
        showToast('Team created successfully', 'success');
        callback(true);
      } else {
        showToast(message || 'Failed to create team', 'error');
        callback(false);
      }
    } catch (error: any) {
      showToast(error.message || 'Error creating team', 'error');
      callback(false);
    }
  };

  const handleTeamFormSubmit = (formData: any, callback: (success: boolean) => void) => {
    let transactionFieldSelect = formData.transactionFieldSelect;
    if (Array.isArray(transactionFieldSelect)) {
      transactionFieldSelect = transactionFieldSelect.map((item: any) =>
        typeof item === "string" ? JSON.parse(item) : item
      );
    }
    const payload = { ...formData, transactionFieldSelect };
    if (teamUid) {
      updateTeamApi(payload, callback);
    } else {
      createTeamApi(payload, callback);
    }
  };
  useEffect(() => {
    fetchSteps();
    fetchConfigurations();
  }, [teamUid]);

  const fetchSteps = async () => {
    if (!teamUid) return;
    try {
      const resp = await getFlowByTeamUid(teamUid);
      if (resp?.data?.steps) {
        setStepOptions(resp.data.steps.map((step: any) => ({
          uid: step._id,
          _id: step._id,
          title: step.title
        })));
      }
    } catch {
      setStepOptions([]);
    }
  };

  const fetchConfigurations = async () => {
    setIsLoadingConfigs(true);
    try {
      const { data, success } = await getIntegrationConfig(teamUid);
      if (success && Array.isArray(data)) {
        setConfigurations(data);
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      showToast('Failed to load configurations', 'error');
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  const handleSubmitIntegration = async (payload: any) => {
    try {
      if (selectedConfigForEdit) {
        const response = await updateIntegrationConfig(selectedConfigForEdit._id, teamUid, payload);
        if (response?.success) {
          showToast('Configuration updated successfully!', 'success');
          await fetchConfigurations();
          handleCancelEdit();
        } else {
          showToast(response?.message || 'Failed to update configuration', 'error');
        }
      } else {
        const response = await createIntegrationConfig(teamUid, payload);
        if (response?.success) {
          showToast('Configuration saved successfully!', 'success');
          await fetchConfigurations();
          handleCancelEdit();
        } else {
          showToast(response?.message || 'Failed to save configuration', 'error');
        }
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
    }
  };

  const handleEdit = (config: IntegrationConfig) => {
    setSelectedConfigForEdit(config);
    setShowForm(true);
  };

  const handleDelete = async (configId: string) => {
    try {
      const response = await deleteIntegrationConfig(configId);
      if (response?.success) {
        showToast('Configuration deleted successfully!', 'success');
        await fetchConfigurations();
        setDeleteConfirmId(null);
      } else {
        showToast(response?.message || 'Failed to delete configuration', 'error');
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
    }
  };

  const handleAddNew = () => {
    setSelectedConfigForEdit(null);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setSelectedConfigForEdit(null);
    setShowForm(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!showForm ? (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <IntegrationList
            configurations={configurations}
            stepOptions={stepOptions}
            isLoading={isLoadingConfigs}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAddNew={handleAddNew}
            theme={theme}
          />
        </Box>
      ) : (
        <IntegrationForm
          selectedConfig={selectedConfigForEdit}
          stepOptions={stepOptions}
          teamData={teamData}
          teamUid={teamUid}
          onSubmit={handleSubmitIntegration}
          onCancel={handleCancelEdit}
          onTeamFormSubmit={handleTeamFormSubmit}
          showToast={showToast}
          theme={theme}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Configuration</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this integration configuration? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (deleteConfirmId) {
                handleDelete(deleteConfirmId);
              }
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmartSheetConfig;